// Code Starts
import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "twilio-video";
import { useNavigate, useLocation } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { app, database, auth, getAuth } from "../firebase";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import {
    collection,
    addDoc,
    doc,
    setDoc,
    getDocs,
    query,
    where,
    onSnapshot,
    orderBy,
    updateDoc,
    limit,
    serverTimestamp,
    getDoc,
} from "firebase/firestore";

// Simple Toast Manager to prevent overlapping toasts
let activeToasts = new Set();

const showToast = (message, type = 'info', options = {}) => {
    // Prevent duplicate toasts
    if (activeToasts.has(message)) return;
    
    activeToasts.add(message);
    
    const toastConfig = {
        
        autoClose: options.duration || 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        onClose: () => activeToasts.delete(message)
    };

    switch(type) {
        case 'error':
            toast.error(message, toastConfig);
            break;
        case 'warning':
            toast.warn(message, toastConfig);
            break;
        case 'success':
            toast.success(message, toastConfig);
            break;
        default:
            toast.info(message, toastConfig);
    }
};

// MIUI Browser Detection
const isMIUIBrowser = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('miuibrowser') || userAgent.includes('xiaomi');
};

// Simple error logging
const logError = (error, context = '') => {
    console.error(`[Mobile${context ? ` - ${context}` : ''}]:`, error);
    // Add Sentry here if available
    if (window.Sentry) {
        window.Sentry.captureException(error, {
            tags: { component: 'Mobile', context }
        });
    }
};

export const Mobile = (props) => {
    const search = useLocation().search;
    const navigate = useNavigate();
    const identityVal = new URLSearchParams(search).get("identity");
    const roomIdVal = new URLSearchParams(search).get("rmid");
    const roomUid = new URLSearchParams(search).get("uid");

    // Enhanced URL decoding with error handling
    let identity, roomId, rmroomId;
    try {
        identity = identityVal ? atob(identityVal) : null;
        roomId = roomIdVal ? atob(roomIdVal) : null;
        rmroomId = roomUid ? atob(roomUid) : null;
    } catch (error) {
        logError(error, 'URL Decoding');
        showToast('Invalid URL parameters. Please use a valid link.', 'error');
    }

    // Enhanced constraints with audio disabled to prevent echo
    const constraints = {
        video: { 
            facingMode: "environment",
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 }
        },
        audio: false // ECHO FIX: Disable audio
    };

    const [show, setShow] = useState(true);
    const [mobileDeviceSettings, setMobileDeviceSettings] = useState([]);
    const [roominfo, setRoom] = useState(null);
    const [battery, setBattery] = useState(100);
    const [isCharging, setCharging] = useState(false);

    const [rearCameraStatus, setRearCamera] = useState('');
    const [networkInfo, setNetworkInfo] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [screenWidth, setScreenWidth] = useState('');
    const [screenHeight, setScreenHeight] = useState('');
    const [deviceOrientation, setOrientation] = useState('');
    const [pageVisibility, setpageVisibility] = useState(true);
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    const [roomSid, setRoomSid] = useState(null);
    const [mobileshow, setMobileshow] = useState(false);
    const [warningcontent, setWarningContent] = useState("Problem with Internet Connection.");
    const [reconnectBtn, setreconnectBtn] = useState(false);
    const reconnectingRef = useRef(false);

    // --- Streaming heartbeat refs ---
    // heartbeatIntervalRef: the setInterval handle writing the heartbeat.
    // roomActiveRef: true only between a successful Twilio connect and disconnect/stop.
    const heartbeatIntervalRef = useRef(null);
    const roomActiveRef = useRef(false);

    // MIUI Browser Warning State
    const [showMIUIWarning, setShowMIUIWarning] = useState(false);

    // Enhanced connection monitoring
    const [connectionAttempts, setConnectionAttempts] = useState(0);
    const [lastNetworkCheck, setLastNetworkCheck] = useState(Date.now());

    // TOKEN STATUS CHECK - Added from second file
    const [roomStatus, setRoomStatus] = useState(null);
    const [isCheckingRoomStatus, setIsCheckingRoomStatus] = useState(false);
    const [roomStatusError, setRoomStatusError] = useState('');
    const [showRoomStatusModal, setShowRoomStatusModal] = useState(false);

    // API endpoint for room status check
    // const ROOM_STATUS_API = 'https://dev.virtualemployee.com/admin-panel/api/v3.0/twilio/get-room-status';

    const ROOM_STATUS_API = 'https://nmve.io/admin-panel/api/v3.0/twilio/get-room-status';

    // TOKEN STATUS CHECK FUNCTION - From second file
    const checkRoomStatus = async (roomIdentity = null) => {
        // Use the decoded rmroomId (which comes from uid parameter) for room status check
        if (!roomIdentity && !rmroomId) {
            console.warn('No room identity available for status check');
            return null;
        }

        const identityToCheck = roomIdentity || rmroomId;
        console.log('Using identity for room status check:', identityToCheck);
        
        try {
            console.log('Checking room status for identity:', identityToCheck);
            setIsCheckingRoomStatus(true);
            setRoomStatusError('');
            
            const response = await axios.post(ROOM_STATUS_API, {
                identity: identityToCheck
            }, {
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 10000 // 10 second timeout
            });

            const data = response.data;
            console.log('Room status response:', data);

            if (data.statusCode === 200) {
                const status = data.status;
                setRoomStatus(status);
                
                console.log(`Room status: ${status}`);
                
                // Return the status for immediate use
                return status;
            } else {
                console.warn('Unexpected response format:', data);
                setRoomStatusError('Unable to determine room status');
                return null;
            }
        } catch (error) {
            console.error('Room status check failed:', error);
            
            let errorMessage = 'Failed to check room status';
            
            if (error.code === 'ECONNABORTED') {
                errorMessage = 'Room status check timed out';
            } else if (error.response) {
                errorMessage = `Server error: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'Network error - please check your connection';
            }
            
            setRoomStatusError(errorMessage);
            setRoomStatus('error');
            return 'error';
        } finally {
            setIsCheckingRoomStatus(false);
        }
    };

    // Get user-friendly message for room status - From second file
    const getRoomStatusMessage = (status) => {
        switch (status) {
            case 'completed':
                return 'The interview session has been completed. You can safely close this page.';
            case 'failed':
                return 'The interview session encountered an error and has been terminated.';
            case 'canceled':
                return 'The interview session has been canceled.';
            case 'error':
                return 'Unable to check the current session status. Please try again or contact support.';
            default:
                return `Session status: ${status}. Please contact support if this continues.`;
        }
    };

    // Validate room status before joining - From second file
    const validateRoomBeforeJoining = async () => {
        if (!rmroomId) {
            showToast('Room ID is missing. Cannot connect to session.', 'error');
            return false;
        }

        console.log('Validating room status before joining with identity:', rmroomId);
        const status = await checkRoomStatus(rmroomId); // Explicitly pass rmroomId
        
        if (!status || status === 'error') {
            setWarningContent('Unable to verify session status. Please try again or contact support.');
            setShowRoomStatusModal(true);
            return false;
        }
        
        if (status !== 'in-progress') {
            console.log('Room is not in progress. Current status:', status);
            setWarningContent(getRoomStatusMessage(status));
            setShowRoomStatusModal(true);
            return false;
        }
        
        console.log('Room status validated successfully:', status);
        return true;
    };

    // Manual room status check function - From second file
    const manualRoomStatusCheck = async () => {
        console.log('Manual room status check initiated');
        const status = await checkRoomStatus(rmroomId);
        
        if (status && status !== 'in-progress' && roominfo) {
            console.log('Room is no longer in progress:', status);
            
            showToast('Session status has changed. Disconnecting...', 'info');
            
            roominfo.disconnect();
            setRoom(null);
            
            setWarningContent(getRoomStatusMessage(status));
            setShowRoomStatusModal(true);
        } else if (status === 'in-progress') {
            showToast('Session is still active and in progress.', 'success');
        }
        
        return status;
    };

    // MIUI Browser Check on component mount
    useEffect(() => {
        if (isMIUIBrowser()) {
            setShowMIUIWarning(true);
        }
    }, []);

    // Initial room status check on component mount - From second file
    useEffect(() => {
        if (rmroomId) {
            console.log('Checking initial room status with identity:', rmroomId);
            checkRoomStatus(rmroomId).then(status => {
                if (status && status !== 'in-progress') {
                    console.log('Room is not in progress on mount:', status);
                    setWarningContent(getRoomStatusMessage(status));
                    setShowRoomStatusModal(true);
                }
            });
        }
    }, [rmroomId]);

    // Network quality monitoring
    useEffect(() => {
        const checkNetworkQuality = () => {
            if (navigator.connection) {
                const connection = navigator.connection;
                const effectiveType = connection.effectiveType;
                
                setNetworkInfo(effectiveType);
                
                // Show warning for poor connection
                if (effectiveType === 'slow-2g' || effectiveType === '2g') {
                    showToast('⚠️ Poor network quality detected. Video quality may be affected.', 'warning', { duration: 6000 });
                }
            }
        };

        checkNetworkQuality();
        
        if (navigator.connection) {
            navigator.connection.addEventListener('change', checkNetworkQuality);
            return () => navigator.connection.removeEventListener('change', checkNetworkQuality);
        }
    }, []);

    // Enhanced online/offline monitoring
    useEffect(() => {
        function handleOnlineStatusChange() {
            const online = navigator.onLine;
            setIsOnline(online);
            
            if (online) {
                showToast('🟢 Network connection restored!', 'success', { duration: 3000 });
                setConnectionAttempts(0);
            } else {
                showToast('🔴 Network connection lost. Please check your internet connection.', 'error', { duration: 8000 });
            }
        }
        
        window.addEventListener('online', handleOnlineStatusChange);
        window.addEventListener('offline', handleOnlineStatusChange);
        
        return () => {
            window.removeEventListener('online', handleOnlineStatusChange);
            window.removeEventListener('offline', handleOnlineStatusChange);
        };
    }, []);

    // Enhanced connection monitoring and reconnection logic
    useEffect(() => {
        if (!isOnline && roominfo) {
            setMobileshow(true);
            setWarningContent("Your internet connection has been lost. Please check your connection and try again.");
            setreconnectBtn(true);
            reconnectingRef.current = true;
        } else if (isOnline && reconnectingRef.current && roomSid) {
            // Auto-reconnect when connection is restored
            setTimeout(() => {
                reconnectToRoom();
                setMobileshow(false);
                setreconnectBtn(false);
                reconnectingRef.current = false;
            }, 2000); // Wait 2 seconds before reconnecting
        }
    }, [isOnline, roominfo, roomSid]);

    // Enhanced updateMobileDeviceSettings with error handling
    const updateMobileDeviceSettings = async () => {
        if (rmroomId && identity) {
            try {
                const docRef = doc(database, "userDevices", rmroomId); // userDevices_Test    userDevices
                const docSnap = await getDoc(docRef);
                
                const data = {
                    identity: identity,
                    battery: battery,
                    isCharging: isCharging,
                    rearCameraStatus: rearCameraStatus,
                    networkInfo: networkInfo,
                    latitude: latitude,
                    longitude: longitude,
                    deviceOrientation: deviceOrientation,
                    pageVisibility: pageVisibility,
                    isOnline: isOnline,
                    roomStatus: roomStatus, // Add room status to Firebase
                    audioEnabled: false, // Document that audio is disabled
                    lastUpdate: new Date().toISOString(),
                    timeStamp: serverTimestamp(),  
                    // mobileConnected: !!roominfo, // newAdded                  
                };
                
                if (docSnap.exists()) {
                    await updateDoc(docRef, data);
                } else {
                    await setDoc(docRef, data);
                    // await setDoc(docRef, data, { merge: true });
                }
            } catch (error) {
                logError(error, 'Firebase Update');
                // Don't show toast for Firebase errors as they happen frequently
            }
        }
    };

    // --- Streaming heartbeat -------------------------------------------------
    // The desktop cannot, on its own, tell an actively-streaming FOREGROUND tab
    // apart from a leftover/minimized tab that is still technically publishing
    // frames (a backgrounded desktop tab keeps its camera alive). To make this
    // deterministic we emit a heartbeat ONLY while this tab is BOTH:
    //   (a) connected to the Twilio room (i.e. "Start Streaming" was pressed), and
    //   (b) in the foreground (document not hidden).
    // The instant this tab is hidden/minimized, disconnected, or stops streaming,
    // the heartbeat stops and we flag isStreaming:false — so the desktop reliably
    // knows THIS session is not actively streaming right now, even if some frames
    // are still trickling in from a backgrounded tab.
    const HEARTBEAT_INTERVAL_MS = 1500;

    const writeStreamState = async (payload) => {
        if (!rmroomId) return;
        try {
            const docRef = doc(database, "userDevices", rmroomId);
            await setDoc(docRef, payload, { merge: true });
        } catch (error) {
            logError(error, 'Stream Heartbeat');
        }
    };

    const startStreamHeartbeat = () => {
        if (heartbeatIntervalRef.current) return; // already beating
        const beat = () => {
            // Only beat while genuinely streaming AND visible.
            if (!roomActiveRef.current || document.hidden) return;
            writeStreamState({ isStreaming: true, streamHeartbeat: Date.now() });
        };
        beat(); // immediate first beat
        heartbeatIntervalRef.current = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    };

    const stopStreamHeartbeat = () => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }
        // Tell the desktop this session is no longer actively streaming.
        writeStreamState({ isStreaming: false });
    };

    // Enhanced camera access with better error handling
    const setupCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            const video = document.querySelector("#rearVideo");
            
            if (video) {
                video.srcObject = stream;
                video.muted = true; // Keep muted
                video.volume = 0;
                video.onloadedmetadata = () => {
                    video.play().catch(e => logError(e, 'Video Play'));
                };
            }
            
            setRearCamera(true);
            return stream;
        } catch (err) {
            logError(err, 'Camera Setup');
            setRearCamera(false);
            
            if (err.name === "NotAllowedError") {
                showToast('Camera access denied. Please allow camera access to continue.', 'error', { duration: 8000 });
            } else if (err.name === "NotFoundError") {
                showToast('Camera not found. Please ensure your device has a camera.', 'error');
            } else {
                showToast('Unable to access camera. Please try again.', 'error');
            }
            
            return null;
        }
    };

    // Enhanced generateToken with retry logic
    async function generateToken(retries = 3) {
        for (let i = 0; i < retries; i++) {
            try {
                const response = await axios.get(
                    `https://live-work-3589-dev.twil.io/token-service?identity=${identity}`,
                    { timeout: 10000 }
                );
                return response.data.accessToken;
            } catch (error) {
                logError(error, `Token Generation Attempt ${i + 1}`);                
                if (i === retries - 1) {
                    throw error;
                }                
                // Wait before retry
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
        }
    }

    // Enhanced room joining with better error handling and status validation
    async function startRoom(roomId) {
        try {
            const roomName = roomId;
            const token = await generateToken();
            
            showToast('Please wait, we are connecting...', 'info', { duration: 3000 });
            
            const room = await joinVideoRoom(roomName, token);
            setRoom(room);
            setRoomSid(room.sid);

            // This session is now actively streaming → start emitting the heartbeat.
            roomActiveRef.current = true;
            startStreamHeartbeat();

            handleConnectedParticipant(room.localParticipant);
            room.participants.forEach(handleConnectedParticipant);
            room.on("participantConnected", handleConnectedParticipant);
            
            room.on('participantDisconnected', (participant) => {
                handleDisconnectedParticipant(participant, room);
            });
            
            // Enhanced room event handlers
            room.on('disconnected', (room, error) => {
                console.log('Room disconnected:', error);
                setRoom(null);

                // Stream is over → stop the heartbeat and flag inactive.
                roomActiveRef.current = false;
                stopStreamHeartbeat();

                if (error) {
                    if (error.code === 53118 || error.message.includes('Room ended')) {
                        setWarningContent('The interview session has ended.');
                        setRoomStatus('completed');
                    } else {
                        setWarningContent('Connection lost. Please try reconnecting.');
                        setRoomStatus('failed');
                    }
                    setShowRoomStatusModal(true);
                }
            });

            room.on('reconnecting', (error) => {
                console.log('Reconnecting to room:', error);
                showToast('Connection lost. Attempting to reconnect...', 'warning');
            });

            room.on('reconnected', () => {
                console.log('Reconnected to room');
                showToast('Successfully reconnected!', 'success');
            });
            
            window.addEventListener("pagehide", () => room.disconnect());
            window.addEventListener("beforeunload", () => room.disconnect());
            
            showToast('Successfully connected to the session!', 'success');
            
        } catch (error) {
            logError(error, 'Start Room');
            showToast('Failed to connect to the session. Please try again.', 'error');
            throw error;
        }
    }

    // Enhanced reconnection with exponential backoff and status validation
    async function reconnectToRoom() {
        if (roomSid && roomId) {
            const maxAttempts = 5;
            setConnectionAttempts(prev => prev + 1);
            
            if (connectionAttempts >= maxAttempts) {
                showToast('Maximum reconnection attempts reached. Please refresh the page.', 'error');
                return;
            }

            try {
                // First validate room status before reconnecting
                const isRoomValid = await validateRoomBeforeJoining();
                if (!isRoomValid) {
                    return;
                }
                
                showToast('Reconnecting to room...', 'info');
                
                const token = await generateToken();
                const room = await joinVideoRoom(roomId, token);
                
                setRoom(room);
                setRoomSid(room.sid);

                // Reconnected and streaming again → resume the heartbeat.
                roomActiveRef.current = true;
                startStreamHeartbeat();

                handleConnectedParticipant(room.localParticipant);
                room.participants.forEach(handleConnectedParticipant);
                room.on("participantConnected", handleConnectedParticipant);
                
                room.on('participantDisconnected', (participant) => {
                    handleDisconnectedParticipant(participant, room);
                });

                room.on('disconnected', () => {
                    // Stream is over → stop the heartbeat and flag inactive.
                    roomActiveRef.current = false;
                    stopStreamHeartbeat();
                });
                
                window.addEventListener("pagehide", () => room.disconnect());
                window.addEventListener("beforeunload", () => room.disconnect());
                
                showToast('Successfully reconnected!', 'success');
                setConnectionAttempts(0); // Reset on successful connection
                
            } catch (error) {
                logError(error, 'Reconnect');
                
                const delay = Math.min(1000 * Math.pow(2, connectionAttempts), 30000);
                showToast(`Reconnection failed. Retrying in ${delay/1000} seconds...`, 'warning');
                
                setTimeout(() => {
                    if (!roominfo && isOnline) {
                        reconnectToRoom();
                    }
                }, delay);
            }
        }
    }

    async function handleConnectedParticipant(participant) {
        participant.tracks.forEach((trackPublication) => {
            handleTrackPublication(trackPublication, participant);
        });
        participant.on("trackPublished", handleTrackPublication);
    }

    // Enhanced track handling with error protection
    async function handleTrackPublication(trackPublication, participant) {
        function displayTrack(track) {
            try {
                // Safe track name setting
                if (track && typeof track.setTrackName === 'function') {
                    track.setTrackName("Mobile_Track");
                }
                
                // ECHO FIX: Mobile device should never publish audio
                if (track && track.kind === 'audio' && participant.identity === identity) {
                    console.warn('Mobile device attempting to publish audio - disabling');
                    if (typeof track.disable === 'function') {
                        track.disable();
                    }
                    return;
                }
            } catch (err) {
                logError(err, 'Track Display');
            }
        }
        
        if (trackPublication && trackPublication.track) {
            displayTrack(trackPublication.track);
        }
        
        if (trackPublication && typeof trackPublication.on === 'function') {
            trackPublication.on("subscribed", displayTrack);
        }
    }

    async function handleDisconnectedParticipant(participant, room) {
        try {
            if (!participant.identity.includes('Admin_')) {
                participant.removeAllListeners();
                if (room) {
                    room.disconnect();
                    navigate('/disconnected');
                }
            }
        } catch (error) {
            logError(error, 'Participant Disconnect');
        }
    }

    // Enhanced joinVideoRoom with echo prevention
    async function joinVideoRoom(roomName, token) {
        try {
            const room = await connect(token, {
                name: roomName,
                audio: false, // ECHO FIX: No audio from mobile
                video: { 
                    facingMode: "environment",
                    width: { ideal: 1280, max: 1920 },
                    height: { ideal: 720, max: 1080 }
                },
                dominantSpeaker: false,
                maxAudioBitrate: 0, // No audio bitrate needed
                maxVideoBitrate: 300000,
                preferredVideoCodecs: ['VP8', 'H264'],
                networkQuality: { local: 1, remote: 1 }
            });
            
            // ECHO FIX: Ensure no audio tracks are published
            if (room.localParticipant && room.localParticipant.audioTracks) {
                room.localParticipant.audioTracks.forEach(audioTrack => {
                    try {
                        audioTrack.track.stop();
                        room.localParticipant.unpublishTrack(audioTrack.track);
                        console.log('Stopped and unpublished audio track from mobile');
                    } catch (error) {
                        logError(error, 'Audio Track Cleanup');
                    }
                });
            }
            
            console.log('Joined room with video only - no audio publishing');
            return room;
        } catch (error) {
            logError(error, 'Join Video Room');
            throw error;
        }
    }

    // Enhanced joinRoom with room status validation
    const joinRoom = async () => {
        try {
            // MAIN CHECK POINT: Validate room status before joining
            console.log('Validating room status before joining...');
            const isRoomValid = await validateRoomBeforeJoining();
            if (!isRoomValid) {
                return;
            }

            // Check camera access first
            const stream = await setupCamera();
            if (!stream) {
                return; // Error already shown in setupCamera
            }

            // Check battery level
            if (battery < 40 && !isCharging) {
                showToast('Battery is below 40% and not charging. Please connect charger before streaming.', 'error', { duration: 8000 });
                return;
            }

            // Check internet connection
            if (!isOnline) {
                showToast('No internet connection. Please check your connection and try again.', 'error');
                return;
            }

            // Check if room ID exists
            if (!roomId) {
                showToast('Unable to connect', 'error');
                return;
            }

            await startRoom(roomId);
            
        } catch (error) {
            logError(error, 'Join Room');
            showToast('Failed to join room. Please try again.', 'error');
        }
    };

    // Manual reconnection function
    const reconnectConnection = () => {
        if (isOnline && roomSid) {
            reconnectToRoom();
            setMobileshow(false);
            setreconnectBtn(false);
            reconnectingRef.current = false;
        }
    };

    // Auto-update device settings
    useEffect(() => {
        updateMobileDeviceSettings();
    }, [battery, isCharging, pageVisibility, isOnline, networkInfo, rearCameraStatus, roomStatus]);

    // Initialize device settings
    useEffect(() => {
        // Setup camera on mount
        setupCamera();
        
        // Battery API
        if (navigator.getBattery) {
            navigator.getBattery().then(function (battery) {
                function updateBatteryStatus() {
                    setBattery(Math.round(battery.level * 100));
                    setCharging(battery.charging);
                }
                battery.addEventListener('levelchange', updateBatteryStatus);
                battery.addEventListener('chargingchange', updateBatteryStatus);
                updateBatteryStatus();
            }).catch(e => logError(e, 'Battery API'));
        }

        // Network information
        if (navigator.connection) {
            const updateNetworkInfo = () => {
                setNetworkInfo(navigator.connection.effectiveType || '');
            };
            navigator.connection.addEventListener('change', updateNetworkInfo);
            updateNetworkInfo();
        }

        // Geolocation
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    setLatitude(position.coords.latitude);
                    setLongitude(position.coords.longitude);
                },
                function (error) {
                    logError(error, 'Geolocation');
                }
            );
        }

        // Screen dimensions
        setScreenWidth(window.screen.width);
        setScreenHeight(window.screen.height);
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            const visible = !document.hidden;
            setpageVisibility(visible);

            // Heartbeat must reflect reality: pause it the instant this tab is hidden/
            // minimized (so the desktop stops seeing a fresh heartbeat and reopens the
            // modal), and resume it only when the tab is foreground AND still streaming.
            if (!visible) {
                stopStreamHeartbeat();
            } else if (roomActiveRef.current) {
                startStreamHeartbeat();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Ensure the heartbeat interval is always torn down on unmount.
    useEffect(() => {
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
        };
    }, []);

    // Enhanced orientation detection
    useEffect(() => {
        const handleOrientationChange = () => {
            setTimeout(() => {
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                const orientationAngle = window.orientation;
                
                let isLandscape = false;
                
                if (typeof orientationAngle !== 'undefined') {
                    isLandscape = Math.abs(orientationAngle) === 90;
                } else {
                    isLandscape = windowWidth > windowHeight;
                }
                
                const newOrientation = isLandscape ? 'Landscape' : 'Portrait';
                setOrientation(newOrientation);
                setScreenWidth(window.screen.width);
                setScreenHeight(window.screen.height);
            }, 200);
        };

        handleOrientationChange();
        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);
        
        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleOrientationChange);
        };
    }, []);

    return (
        <>
            <div className="mobile-stream-page">
                <div className="top-header bg-primary sticky-top py-2">
                    <div className="navbar-brand d-flex align-items-center justify-content-center p-0">
                        <div className="brand-name me-2">NM-Interview System</div>
                        <div className="app-version d-flex gap-1 small">
                            <span className="badge bg-warning text-dark">BETA</span>
                            <span className="badge bg-light text-dark">v1.2.3</span>
                        </div>
                    </div>
                </div>
                
                <div className="container-xl p-2">
                    <div className="custom-wrapper mx-auto" style={{ maxWidth: '700px' }}>
                        <div className="preview-wrapper ratio ratio-4x3 overflow-hidden position-relative">
                            <div id="video-preview" className="object-fit-cover"></div>
                            <video 
                                className="w-100 object-fit-cover" 
                                width="640" 
                                height="480" 
                                id="rearVideo" 
                                muted 
                                playsInline
                                style={{ backgroundColor: '#000' }}
                            ></video>

                            {/* Overlay for loading state */}
                            {isCheckingRoomStatus && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50">
                                    <div className="text-white text-center">
                                        <div className="spinner-border spinner-border-sm mb-2" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div className="small">Validating session status...</div>
                                    </div>
                                </div>
                            )}

                            {/* Status indicators */}
                            <div className="position-absolute top-0 end-0 p-2">
                                <div className="d-flex flex-column gap-1">
                                    {/* Room status indicator */}
                                    {roomStatus && (
                                        <span className={`badge ${roomStatus === 'in-progress' ? 'bg-success' : roomStatus === 'completed' ? 'bg-secondary' : 'bg-warning'} small`}>
                                            <i className={`bi ${roomStatus === 'in-progress' ? 'bi-record-circle' : roomStatus === 'completed' ? 'bi-check-circle' : 'bi-exclamation-circle'}`}></i>
                                            {roomStatus === 'in-progress' ? 'Live' : roomStatus}
                                        </span>
                                    )}

                                    {/* Battery indicator */}
                                    <span className={`badge ${battery < 20 ? 'bg-danger' : battery < 40 ? 'bg-warning' : 'bg-success'} small`}>
                                        <i className={`bi ${isCharging ? 'bi-battery-charging' : 'bi-battery'}`}></i>
                                        {Math.round(battery)}%
                                    </span>
                                    
                                    {/* Network indicator */}
                                    <span className={`badge ${isOnline ? 'bg-success' : 'bg-danger'} small`}>
                                        <i className={`bi ${isOnline ? 'bi-wifi' : 'bi-wifi-off'}`}></i>
                                        {networkInfo || (isOnline ? 'Online' : 'Offline')}
                                    </span>
                                    
                                    {/* Audio disabled indicator */}
                                    <span className="badge bg-secondary small" title="Audio disabled to prevent echo">
                                        <i className="bi bi-mic-mute"></i>
                                        Audio Off
                                    </span>
                                </div>
                            </div>

                            <div className="relative-wrapper text-center p-3">
                                <Alert 
                                    show={show} 
                                    variant="info" 
                                    onClose={() => setShow(false)} 
                                    dismissible 
                                    className="small mx-auto mb-3" 
                                    style={{ maxWidth: '350px' }}
                                >
                                    <p className="m-0">
                                        Position your camera so both you and your system are visible, then click the green button.
                                        <br />
                                        <small className="text-muted">
                                            <i className="bi bi-mic-mute me-1"></i>
                                            Audio is disabled on mobile to prevent echo feedback.
                                        </small>
                                    </p>
                                </Alert>

                                <div className="btn-wrapper d-flex flex-wrap justify-content-center gap-2">
                                    {roominfo ? (
                                        <button 
                                            className="btn btn-danger focus-ring focus-ring-danger rounded-pill" 
                                            title="Stop Streaming" 
                                            onClick={() => {
                                                if (roominfo) {
                                                    roominfo.disconnect();
                                                    navigate('/disconnected');
                                                }
                                            }}
                                        >
                                            <i className="bi bi-stop-circle-fill me-2"></i> 
                                            Stop Streaming
                                        </button>
                                    ) : (
                                        <button 
                                            className="btn btn-success focus-ring focus-ring-success rounded-pill" 
                                            title="Start Streaming" 
                                            onClick={joinRoom}
                                            disabled={isCheckingRoomStatus}
                                        >
                                            <i className="bi bi-play-circle-fill me-2"></i> 
                                            {isCheckingRoomStatus ? 'Validating Session...' : 'Start Streaming'}
                                        </button>
                                    )}
                                </div>

                                {/* Manual status check button for debugging */}
                                {!roominfo && (
                                    <div className="mt-2">
                                        <button 
                                            className="btn btn-outline-info btn-sm" 
                                            onClick={manualRoomStatusCheck}
                                            disabled={isCheckingRoomStatus}
                                        >
                                            <i className="bi bi-arrow-clockwise me-1"></i>
                                            {isCheckingRoomStatus ? 'Checking...' : 'Check Session Status'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* MIUI Browser Warning Modal */}
            <Modal 
                centered 
                show={showMIUIWarning} 
                backdrop="static" 
                keyboard={false} 
                contentClassName="rounded-3"
            >
                <Modal.Body className="border-0 p-4">
                    <div className="text-center">
                        <div className="text-danger mb-3">
                            <i className="bi bi-exclamation-triangle-fill display-1"></i>
                        </div>
                        <h5 className="fw-bold mb-3 text-danger">Unsupported Browser</h5>
                        <div className="text-muted mb-4">
                            MIUI Browser is not compatible with this application. 
                            Please use a modern browser like <strong>Chrome</strong>, <strong>Firefox</strong>, or <strong>Safari</strong> for the best experience.
                        </div>
                        <div className="d-grid">
                            <button 
                                className="btn btn-primary btn-lg" 
                                onClick={() => setShowMIUIWarning(false)}
                            >
                                <i className="bi bi-check-lg me-2"></i>
                                I Understand
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Enhanced Connection Issues Modal */}
            <Modal 
                centered 
                show={mobileshow} 
                backdrop="static" 
                keyboard={false} 
                contentClassName="rounded-3"
            >
                <Modal.Body className="border-0 p-4">
                    <div className="text-center">
                        <div className="text-warning mb-3">
                            <i className="bi bi-wifi-off display-1"></i>
                        </div>
                        <h5 className="fw-bold mb-3">Connection Issue</h5>
                        <div className="text-muted mb-4">
                            {warningcontent}
                        </div>
                        {reconnectBtn && (
                            <div className="d-grid gap-2">
                                <button 
                                    className="btn btn-primary btn-lg" 
                                    onClick={reconnectConnection}
                                    disabled={!isOnline}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    {isOnline ? 'Reconnect' : 'Waiting for connection...'}
                                </button>
                                
                                {connectionAttempts > 0 && (
                                    <small className="text-muted">
                                        Attempt {connectionAttempts} of 5
                                    </small>
                                )}
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Room Status Modal - Added from second file */}
            <Modal 
                centered 
                show={showRoomStatusModal} 
                backdrop="static" 
                keyboard={false} 
                contentClassName="rounded-3"
            >
                <Modal.Body className="border-0 p-4">
                    <div className="text-center">
                        <div className={`mb-3 ${roomStatus === 'completed' ? 'text-success' : roomStatus === 'error' ? 'text-danger' : 'text-warning'}`}>
                            <i className={`display-1 bi ${roomStatus === 'completed' ? 'bi-check-circle' : roomStatus === 'error' ? 'bi-x-circle' : 'bi-exclamation-circle'}`}></i>
                        </div>
                        <h5 className="fw-bold mb-3">Session Status Update</h5>
                        <div className="text-muted mb-4">
                            {warningcontent}
                        </div>
                        <div className="d-grid gap-2">
                            <button 
                                className="btn btn-primary btn-lg" 
                                onClick={() => {
                                    setShowRoomStatusModal(false);
                                    if (roomStatus !== 'in-progress') {
                                        navigate('/disconnected');
                                    }
                                }}
                            >
                                <i className="bi bi-check me-2"></i>
                                {roomStatus === 'in-progress' ? 'Continue' : 'Close'}
                            </button>
                            
                            {roomStatus !== 'completed' && roomStatus !== 'in-progress' && (
                                <button 
                                    className="btn btn-outline-secondary" 
                                    onClick={async () => {
                                        setShowRoomStatusModal(false);
                                        await checkRoomStatus(rmroomId);
                                    }}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Check Again
                                </button>
                            )}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} theme="colored" />

            <style>{`
                .mobile-stream-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                }
                
                .preview-wrapper {
                    border: 3px solid #dee2e6;
                    background: #000;
                    border-radius: 0.5rem;
                }
                
                .badge.small {
                    font-size: 0.7em;
                    padding: 0.25em 0.4em;
                }
                
                #rearVideo {
                    border-radius: inherit;
                }
                
                .btn {
                    touch-action: manipulation;
                    -webkit-tap-highlight-color: transparent;
                }
                
                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .spinner-border-sm {
                    width: 1.5rem;
                    height: 1.5rem;
                }
                
                @media (max-width: 576px) {
                    .container-xl {
                        padding: 0.5rem;
                    }
                    
                    .brand-name {
                        font-size: 14px;
                    }

                    .preview-wrapper {
                       border-width: 2px;
                    }
                    
                    .btn-lg {
                        font-size: 16px;
                        padding: 12px 24px;
                        min-height: 50px;
                    }
                    
                    .modal-dialog {
                        margin: 1rem;
                    }
                }
                
                /* Loading overlay styling */
                .bg-dark.bg-opacity-50 {
                    background-color: rgba(0, 0, 0, 0.7) !important;
                }
                
                /* Touch-friendly buttons */
                .btn-sm {
                    min-height: 32px;
                    font-size: 0.875rem;
                }
                
                /* Status indicator enhancements */
                .badge i {
                    margin-right: 0.25em;
                }
                
                /* Modal enhancements */
                .modal-body {
                    max-height: 90vh;
                    overflow-y: auto;
                }
                
                /* Room status specific styling */
                .badge.bg-success i.bi-record-circle {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.5; }
                    100% { opacity: 1; }
                }
                
                /* Enhanced responsive design */
                @media (max-width: 480px) {
                    .display-1 {
                        font-size: 3rem;
                    }
                    
                    h5 {
                        font-size: 1.1rem;
                    }
                    
                    .small {
                        font-size: 0.8rem;
                    }
                    
                    .btn-wrapper {
                        gap: 0.5rem;
                    }
                }
                
                /* Landscape mode optimizations */
                @media (orientation: landscape) and (max-height: 500px) {
                    .preview-wrapper {
                        max-height: 40vh;
                    }
                    
                    .container-xl {
                        padding: 0.25rem;
                    }
                    
                    .btn-lg {
                        padding: 8px 20px;
                        font-size: 14px;
                    }
                }
                
                /* Performance optimizations */
                .preview-wrapper video {
                    transform: translateZ(0);
                    -webkit-transform: translateZ(0);
                    will-change: transform;
                }
                
                /* Accessibility improvements */
                .btn:focus {
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                .btn-success:focus {
                    box-shadow: 0 0 0 0.25rem rgba(25, 135, 84, 0.25);
                }
                
                .btn-danger:focus {
                    box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
                }
                
                /* Enhanced button states */
                .btn-success:not(:disabled):hover {
                    background-color: #198754;
                    border-color: #146c43;
                }
                
                .btn-danger:not(:disabled):hover {
                    background-color: #bb2d3b;
                    border-color: #b02a37;
                }
                
                .btn-outline-info:hover {
                    background-color: #0dcaf0;
                    border-color: #0dcaf0;
                    color: #000;
                }
                
                /* Toast container mobile optimization */
                .Toastify__toast-container {
                    bottom: 1rem;
                    left: 1rem;
                    right: 1rem;
                    width: auto;
                }
                
                /* Safe area support for modern mobile devices */
                .top-header {
                    padding-top: max(8px, env(safe-area-inset-top));
                }
                
                .container-xl {
                    padding-bottom: max(8px, env(safe-area-inset-bottom));
                }
            `}</style>
        </>
    );
};

export default Mobile;
// Code end




// Code Starts
// import axios from "axios";
// import React, { useEffect, useState, useRef } from "react";
// import { connect } from "twilio-video";
// import { useNavigate, useLocation } from 'react-router-dom';
// import Alert from 'react-bootstrap/Alert';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { app, database, auth, getAuth } from "../firebase";
// import Modal from 'react-bootstrap/Modal';
// import Button from 'react-bootstrap/Button';
// import {
//     collection,
//     addDoc,
//     doc,
//     setDoc,
//     getDocs,
//     query,
//     where,
//     onSnapshot,
//     orderBy,
//     updateDoc,
//     limit,
//     serverTimestamp,
//     getDoc,
// } from "firebase/firestore";

// // Simple Toast Manager to prevent overlapping toasts
// let activeToasts = new Set();

// const showToast = (message, type = 'info', options = {}) => {
//     // Prevent duplicate toasts
//     if (activeToasts.has(message)) return;
    
//     activeToasts.add(message);
    
//     const toastConfig = {
//         
//         autoClose: options.duration || 5000,
//         hideProgressBar: false,
//         closeOnClick: true,
//         pauseOnHover: true,
//         draggable: true,
//         onClose: () => activeToasts.delete(message)
//     };

//     switch(type) {
//         case 'error':
//             toast.error(message, toastConfig);
//             break;
//         case 'warning':
//             toast.warn(message, toastConfig);
//             break;
//         case 'success':
//             toast.success(message, toastConfig);
//             break;
//         default:
//             toast.info(message, toastConfig);
//     }
// };

// // MIUI Browser Detection
// const isMIUIBrowser = () => {
//     const userAgent = navigator.userAgent.toLowerCase();
//     return userAgent.includes('miuibrowser') || userAgent.includes('xiaomi');
// };

// // Simple error logging
// const logError = (error, context = '') => {
//     console.error(`[Mobile${context ? ` - ${context}` : ''}]:`, error);
//     // Add Sentry here if available
//     if (window.Sentry) {
//         window.Sentry.captureException(error, {
//             tags: { component: 'Mobile', context }
//         });
//     }
// };

// export const Mobile = (props) => {
//     const search = useLocation().search;
//     const navigate = useNavigate();
//     const identityVal = new URLSearchParams(search).get("identity");
//     const roomIdVal = new URLSearchParams(search).get("rmid");
//     const roomUid = new URLSearchParams(search).get("uid");

//     // Enhanced URL decoding with error handling
//     let identity, roomId, rmroomId;
//     try {
//         identity = identityVal ? atob(identityVal) : null;
//         roomId = roomIdVal ? atob(roomIdVal) : null;
//         rmroomId = roomUid ? atob(roomUid) : null;
//     } catch (error) {
//         logError(error, 'URL Decoding');
//         showToast('Invalid URL parameters. Please use a valid link.', 'error');
//     }

//     // Enhanced constraints with audio disabled to prevent echo
//     const constraints = {
//         video: { 
//             facingMode: "environment",
//             width: { ideal: 1280, max: 1920 },
//             height: { ideal: 720, max: 1080 }
//         },
//         audio: false // ECHO FIX: Disable audio
//     };

//     const [show, setShow] = useState(true);
//     const [mobileDeviceSettings, setMobileDeviceSettings] = useState([]);
//     const [roominfo, setRoom] = useState(null);
//     const [battery, setBattery] = useState(100);
//     const [isCharging, setCharging] = useState(false);

//     const [rearCameraStatus, setRearCamera] = useState('');
//     const [networkInfo, setNetworkInfo] = useState('');
//     const [latitude, setLatitude] = useState('');
//     const [longitude, setLongitude] = useState('');
//     const [screenWidth, setScreenWidth] = useState('');
//     const [screenHeight, setScreenHeight] = useState('');
//     const [deviceOrientation, setOrientation] = useState('');
//     const [pageVisibility, setpageVisibility] = useState(true);
//     const [isOnline, setIsOnline] = useState(navigator.onLine);

//     const [roomSid, setRoomSid] = useState(null);
//     const [mobileshow, setMobileshow] = useState(false);
//     const [warningcontent, setWarningContent] = useState("Problem with Internet Connection.");
//     const [reconnectBtn, setreconnectBtn] = useState(false);
//     const reconnectingRef = useRef(false);

//     // MIUI Browser Warning State
//     const [showMIUIWarning, setShowMIUIWarning] = useState(false);

//     // Enhanced connection monitoring
//     const [connectionAttempts, setConnectionAttempts] = useState(0);
//     const [lastNetworkCheck, setLastNetworkCheck] = useState(Date.now());

//     // MIUI Browser Check on component mount
//     useEffect(() => {
//         if (isMIUIBrowser()) {
//             setShowMIUIWarning(true);
//         }
//     }, []);

//     // Network quality monitoring
//     useEffect(() => {
//         const checkNetworkQuality = () => {
//             if (navigator.connection) {
//                 const connection = navigator.connection;
//                 const effectiveType = connection.effectiveType;
                
//                 setNetworkInfo(effectiveType);
                
//                 // Show warning for poor connection
//                 if (effectiveType === 'slow-2g' || effectiveType === '2g') {
//                     showToast('⚠️ Poor network quality detected. Video quality may be affected.', 'warning', { duration: 6000 });
//                 }
//             }
//         };

//         checkNetworkQuality();
        
//         if (navigator.connection) {
//             navigator.connection.addEventListener('change', checkNetworkQuality);
//             return () => navigator.connection.removeEventListener('change', checkNetworkQuality);
//         }
//     }, []);

//     // Enhanced online/offline monitoring
//     useEffect(() => {
//         function handleOnlineStatusChange() {
//             const online = navigator.onLine;
//             setIsOnline(online);
            
//             if (online) {
//                 showToast('🟢 Network connection restored!', 'success', { duration: 3000 });
//                 setConnectionAttempts(0);
//             } else {
//                 showToast('🔴 Network connection lost. Please check your internet connection.', 'error', { duration: 8000 });
//             }
//         }
        
//         window.addEventListener('online', handleOnlineStatusChange);
//         window.addEventListener('offline', handleOnlineStatusChange);
        
//         return () => {
//             window.removeEventListener('online', handleOnlineStatusChange);
//             window.removeEventListener('offline', handleOnlineStatusChange);
//         };
//     }, []);

//     // Enhanced connection monitoring and reconnection logic
//     useEffect(() => {
//         if (!isOnline && roominfo) {
//             setMobileshow(true);
//             setWarningContent("Your internet connection has been lost. Please check your connection and try again.");
//             setreconnectBtn(true);
//             reconnectingRef.current = true;
//         } else if (isOnline && reconnectingRef.current && roomSid) {
//             // Auto-reconnect when connection is restored
//             setTimeout(() => {
//                 reconnectToRoom();
//                 setMobileshow(false);
//                 setreconnectBtn(false);
//                 reconnectingRef.current = false;
//             }, 2000); // Wait 2 seconds before reconnecting
//         }
//     }, [isOnline, roominfo, roomSid]);

//     // Enhanced updateMobileDeviceSettings with error handling
//     const updateMobileDeviceSettings = async () => {
//         if (rmroomId && identity) {
//             try {
//                 const docRef = doc(database, "userDevices", rmroomId);   
//                 const docSnap = await getDoc(docRef);
                
//                 const data = {
//                     identity: identity,
//                     battery: battery,
//                     isCharging: isCharging,
//                     rearCameraStatus: rearCameraStatus,
//                     networkInfo: networkInfo,
//                     latitude: latitude,
//                     longitude: longitude,
//                     deviceOrientation: deviceOrientation,
//                     pageVisibility: pageVisibility,
//                     isOnline: isOnline,
//                     audioEnabled: false, // Document that audio is disabled
//                     lastUpdate: new Date().toISOString(),
//                     timeStamp: serverTimestamp(),  
//                     // mobileConnected: !!roominfo, // newAdded                  
//                 };
                
//                 if (docSnap.exists()) {
//                     await updateDoc(docRef, data);
//                 } else {
//                     await setDoc(docRef, data);
//                     // await setDoc(docRef, data, { merge: true });
//                 }
//             } catch (error) {
//                 logError(error, 'Firebase Update');
//                 // Don't show toast for Firebase errors as they happen frequently
//             }
//         }
//     };

//     // Enhanced camera access with better error handling
//     const setupCamera = async () => {
//         try {
//             const stream = await navigator.mediaDevices.getUserMedia(constraints);
//             const video = document.querySelector("#rearVideo");
            
//             if (video) {
//                 video.srcObject = stream;
//                 video.muted = true; // Keep muted
//                 video.volume = 0;
//                 video.onloadedmetadata = () => {
//                     video.play().catch(e => logError(e, 'Video Play'));
//                 };
//             }
            
//             setRearCamera(true);
//             return stream;
//         } catch (err) {
//             logError(err, 'Camera Setup');
//             setRearCamera(false);
            
//             if (err.name === "NotAllowedError") {
//                 showToast('Camera access denied. Please allow camera access to continue.', 'error', { duration: 8000 });
//             } else if (err.name === "NotFoundError") {
//                 showToast('Camera not found. Please ensure your device has a camera.', 'error');
//             } else {
//                 showToast('Unable to access camera. Please try again.', 'error');
//             }
            
//             return null;
//         }
//     };

//     // Enhanced generateToken with retry logic
//     async function generateToken(retries = 3) {
//         for (let i = 0; i < retries; i++) {
//             try {
//                 const response = await axios.get(
//                     `https://live-work-3589-dev.twil.io/token-service?identity=${identity}`,
//                     { timeout: 10000 }
//                 );
//                 return response.data.accessToken;
//             } catch (error) {
//                 logError(error, `Token Generation Attempt ${i + 1}`);                
//                 if (i === retries - 1) {
//                     throw error;
//                 }                
//                 // Wait before retry
//                 await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
//             }
//         }
//     }

//     // Enhanced room joining with better error handling
//     async function startRoom(roomId) {
//         try {
//             const roomName = roomId;
//             const token = await generateToken();
            
//             showToast('Please wait, we are connecting...', 'info', { duration: 3000 });
            
//             const room = await joinVideoRoom(roomName, token);
//             setRoom(room);
//             setRoomSid(room.sid);
            
//             handleConnectedParticipant(room.localParticipant);
//             room.participants.forEach(handleConnectedParticipant);
//             room.on("participantConnected", handleConnectedParticipant);
            
//             room.on('participantDisconnected', (participant) => {
//                 handleDisconnectedParticipant(participant, room);
//             });
            
//             // Enhanced room event handlers
//             room.on('disconnected', (room, error) => {
//                 console.log('Room disconnected:', error);
//                 setRoom(null);
                
//                 if (error) {
//                     if (error.code === 53118 || error.message.includes('Room ended')) {
//                         showToast('The interview session has ended.', 'info');
//                     } else {
//                         showToast('Connection lost. Please try reconnecting.', 'warning');
//                     }
//                 }
//             });

//             room.on('reconnecting', (error) => {
//                 console.log('Reconnecting to room:', error);
//                 showToast('Connection lost. Attempting to reconnect...', 'warning');
//             });

//             room.on('reconnected', () => {
//                 console.log('Reconnected to room');
//                 showToast('Successfully reconnected!', 'success');
//             });
            
//             window.addEventListener("pagehide", () => room.disconnect());
//             window.addEventListener("beforeunload", () => room.disconnect());
            
//             showToast('Successfully connected to the session!', 'success');
            
//         } catch (error) {
//             logError(error, 'Start Room');
//             showToast('Failed to connect to the session. Please try again.', 'error');
//             throw error;
//         }
//     }

//     // Enhanced reconnection with exponential backoff
//     async function reconnectToRoom() {
//         if (roomSid && roomId) {
//             const maxAttempts = 5;
//             setConnectionAttempts(prev => prev + 1);
            
//             if (connectionAttempts >= maxAttempts) {
//                 showToast('Maximum reconnection attempts reached. Please refresh the page.', 'error');
//                 return;
//             }
            
//             try {
//                 showToast('Reconnecting to room...', 'info');
                
//                 const token = await generateToken();
//                 const room = await joinVideoRoom(roomId, token);
                
//                 setRoom(room);
//                 setRoomSid(room.sid);
                
//                 handleConnectedParticipant(room.localParticipant);
//                 room.participants.forEach(handleConnectedParticipant);
//                 room.on("participantConnected", handleConnectedParticipant);
                
//                 room.on('participantDisconnected', (participant) => {
//                     handleDisconnectedParticipant(participant, room);
//                 });
                
//                 window.addEventListener("pagehide", () => room.disconnect());
//                 window.addEventListener("beforeunload", () => room.disconnect());
                
//                 showToast('Successfully reconnected!', 'success');
//                 setConnectionAttempts(0); // Reset on successful connection
                
//             } catch (error) {
//                 logError(error, 'Reconnect');
                
//                 const delay = Math.min(1000 * Math.pow(2, connectionAttempts), 30000);
//                 showToast(`Reconnection failed. Retrying in ${delay/1000} seconds...`, 'warning');
                
//                 setTimeout(() => {
//                     if (!roominfo && isOnline) {
//                         reconnectToRoom();
//                     }
//                 }, delay);
//             }
//         }
//     }

//     async function handleConnectedParticipant(participant) {
//         participant.tracks.forEach((trackPublication) => {
//             handleTrackPublication(trackPublication, participant);
//         });
//         participant.on("trackPublished", handleTrackPublication);
//     }

//     // Enhanced track handling with error protection
//     async function handleTrackPublication(trackPublication, participant) {
//         function displayTrack(track) {
//             try {
//                 // Safe track name setting
//                 if (track && typeof track.setTrackName === 'function') {
//                     track.setTrackName("Mobile_Track");
//                 }
                
//                 // ECHO FIX: Mobile device should never publish audio
//                 if (track && track.kind === 'audio' && participant.identity === identity) {
//                     console.warn('Mobile device attempting to publish audio - disabling');
//                     if (typeof track.disable === 'function') {
//                         track.disable();
//                     }
//                     return;
//                 }
//             } catch (err) {
//                 logError(err, 'Track Display');
//             }
//         }
        
//         if (trackPublication && trackPublication.track) {
//             displayTrack(trackPublication.track);
//         }
        
//         if (trackPublication && typeof trackPublication.on === 'function') {
//             trackPublication.on("subscribed", displayTrack);
//         }
//     }

//     async function handleDisconnectedParticipant(participant, room) {
//         try {
//             if (!participant.identity.includes('Admin_')) {
//                 participant.removeAllListeners();
//                 if (room) {
//                     room.disconnect();
//                     navigate('/screen');
//                 }
//             }
//         } catch (error) {
//             logError(error, 'Participant Disconnect');
//         }
//     }

//     // Enhanced joinVideoRoom with echo prevention
//     async function joinVideoRoom(roomName, token) {
//         try {
//             const room = await connect(token, {
//                 name: roomName,
//                 audio: false, // ECHO FIX: No audio from mobile
//                 video: { 
//                     facingMode: "environment",
//                     width: { ideal: 1280, max: 1920 },
//                     height: { ideal: 720, max: 1080 }
//                 },
//                 dominantSpeaker: false,
//                 maxAudioBitrate: 0, // No audio bitrate needed
//                 maxVideoBitrate: 300000,
//                 preferredVideoCodecs: ['VP8', 'H264'],
//                 networkQuality: { local: 1, remote: 1 }
//             });
            
//             // ECHO FIX: Ensure no audio tracks are published
//             if (room.localParticipant && room.localParticipant.audioTracks) {
//                 room.localParticipant.audioTracks.forEach(audioTrack => {
//                     try {
//                         audioTrack.track.stop();
//                         room.localParticipant.unpublishTrack(audioTrack.track);
//                         console.log('Stopped and unpublished audio track from mobile');
//                     } catch (error) {
//                         logError(error, 'Audio Track Cleanup');
//                     }
//                 });
//             }
            
//             console.log('Joined room with video only - no audio publishing');
//             return room;
//         } catch (error) {
//             logError(error, 'Join Video Room');
//             throw error;
//         }
//     }

//     // Enhanced joinRoom with better validation
//     const joinRoom = async () => {
//         try {
//             // Check camera access first
//             const stream = await setupCamera();
//             if (!stream) {
//                 return; // Error already shown in setupCamera
//             }

//             // Check battery level
//             if (battery < 40 && !isCharging) {
//                 showToast('Battery is below 40% and not charging. Please connect charger before streaming.', 'error', { duration: 8000 });
//                 return;
//             }

//             // Check internet connection
//             if (!isOnline) {
//                 showToast('No internet connection. Please check your connection and try again.', 'error');
//                 return;
//             }

//             // Check if room ID exists
//             if (!roomId) {
//                 showToast('Unable to connect', 'error');
//                 return;
//             }

//             await startRoom(roomId);
            
//         } catch (error) {
//             logError(error, 'Join Room');
//             showToast('Failed to join room. Please try again.', 'error');
//         }
//     };

//     // Manual reconnection function
//     const reconnectConnection = () => {
//         if (isOnline && roomSid) {
//             reconnectToRoom();
//             setMobileshow(false);
//             setreconnectBtn(false);
//             reconnectingRef.current = false;
//         }
//     };

//     // Auto-update device settings
//     useEffect(() => {
//         updateMobileDeviceSettings();
//     }, [battery, isCharging, pageVisibility, isOnline, networkInfo, rearCameraStatus]);

//     // Initialize device settings
//     useEffect(() => {
//         // Setup camera on mount
//         setupCamera();
        
//         // Battery API
//         if (navigator.getBattery) {
//             navigator.getBattery().then(function (battery) {
//                 function updateBatteryStatus() {
//                     setBattery(Math.round(battery.level * 100));
//                     setCharging(battery.charging);
//                 }
//                 battery.addEventListener('levelchange', updateBatteryStatus);
//                 battery.addEventListener('chargingchange', updateBatteryStatus);
//                 updateBatteryStatus();
//             }).catch(e => logError(e, 'Battery API'));
//         }

//         // Network information
//         if (navigator.connection) {
//             const updateNetworkInfo = () => {
//                 setNetworkInfo(navigator.connection.effectiveType || '');
//             };
//             navigator.connection.addEventListener('change', updateNetworkInfo);
//             updateNetworkInfo();
//         }

//         // Geolocation
//         if (navigator.geolocation) {
//             navigator.geolocation.getCurrentPosition(
//                 function (position) {
//                     setLatitude(position.coords.latitude);
//                     setLongitude(position.coords.longitude);
//                 },
//                 function (error) {
//                     logError(error, 'Geolocation');
//                 }
//             );
//         }

//         // Screen dimensions
//         setScreenWidth(window.screen.width);
//         setScreenHeight(window.screen.height);
//     }, []);

//     useEffect(() => {
//         const handleVisibilityChange = () => {
//             setpageVisibility(!document.hidden);
//         };
//         document.addEventListener('visibilitychange', handleVisibilityChange);
//         return () => {
//             document.removeEventListener('visibilitychange', handleVisibilityChange);
//         };
//     }, []);

//     // Enhanced orientation detection
//     useEffect(() => {
//         const handleOrientationChange = () => {
//             setTimeout(() => {
//                 const windowWidth = window.innerWidth;
//                 const windowHeight = window.innerHeight;
//                 const orientationAngle = window.orientation;
                
//                 let isLandscape = false;
                
//                 if (typeof orientationAngle !== 'undefined') {
//                     isLandscape = Math.abs(orientationAngle) === 90;
//                 } else {
//                     isLandscape = windowWidth > windowHeight;
//                 }
                
//                 const newOrientation = isLandscape ? 'Landscape' : 'Portrait';
//                 setOrientation(newOrientation);
//                 setScreenWidth(window.screen.width);
//                 setScreenHeight(window.screen.height);
//             }, 200);
//         };

//         handleOrientationChange();
//         window.addEventListener('orientationchange', handleOrientationChange);
//         window.addEventListener('resize', handleOrientationChange);
        
//         return () => {
//             window.removeEventListener('orientationchange', handleOrientationChange);
//             window.removeEventListener('resize', handleOrientationChange);
//         };
//     }, []);

//     return (
//         <>
//             <div className="mobile-stream-page">
//                 <div className="top-header bg-primary sticky-top py-2">
//                     <div className="navbar-brand d-flex align-items-center justify-content-center p-0">
//                         <div className="brand-name me-2">NM-Interview System</div>
//                         <div className="app-version d-flex gap-1 small">
//                             <span className="badge bg-warning text-dark">BETA</span>
//                             <span className="badge bg-light text-dark">v1.2.1</span>
//                         </div>
//                     </div>
//                 </div>
                
//                 <div className="container-xl p-2">
//                     <div className="custom-wrapper mx-auto" style={{ maxWidth: '700px' }}>
//                         <div className="preview-wrapper ratio ratio-4x3 overflow-hidden position-relative">
//                             <div id="video-preview" className="object-fit-cover"></div>
//                             <video 
//                                 className="w-100 object-fit-cover" 
//                                 width="640" 
//                                 height="480" 
//                                 id="rearVideo" 
//                                 muted 
//                                 playsInline
//                                 style={{ backgroundColor: '#000' }}
//                             ></video>

//                             {/* Status indicators */}
//                             <div className="position-absolute top-0 end-0 p-2">
//                                 <div className="d-flex flex-column gap-1">
//                                     {/* Battery indicator */}
//                                     <span className={`badge ${battery < 20 ? 'bg-danger' : battery < 40 ? 'bg-warning' : 'bg-success'} small`}>
//                                         <i className={`bi ${isCharging ? 'bi-battery-charging' : 'bi-battery'}`}></i>
//                                         {Math.round(battery)}%
//                                     </span>
                                    
//                                     {/* Network indicator */}
//                                     <span className={`badge ${isOnline ? 'bg-success' : 'bg-danger'} small`}>
//                                         <i className={`bi ${isOnline ? 'bi-wifi' : 'bi-wifi-off'}`}></i>
//                                         {networkInfo || (isOnline ? 'Online' : 'Offline')}
//                                     </span>
                                    
//                                     {/* Audio disabled indicator */}
//                                     <span className="badge bg-secondary small" title="Audio disabled to prevent echo">
//                                         <i className="bi bi-mic-mute"></i>
//                                         Audio Off
//                                     </span>
//                                 </div>
//                             </div>

//                             <div className="relative-wrapper text-center p-3">
//                                 <Alert 
//                                     show={show} 
//                                     variant="info" 
//                                     onClose={() => setShow(false)} 
//                                     dismissible 
//                                     className="small mx-auto mb-3" 
//                                     style={{ maxWidth: '350px' }}
//                                 >
//                                     <p className="m-0">
//                                         Position your camera so both you and your system are visible, then click the green button.
//                                         <br />
//                                         <small className="text-muted">
//                                             <i className="bi bi-mic-mute me-1"></i>
//                                             Audio is disabled on mobile to prevent echo feedback.
//                                         </small>
//                                     </p>
//                                 </Alert>

//                                 <div className="btn-wrapper d-flex flex-wrap justify-content-center gap-2">
//                                     {roominfo ? (
//                                         <button 
//                                             className="btn btn-danger focus-ring focus-ring-danger rounded-pill" 
//                                             title="Stop Streaming" 
//                                             onClick={() => {
//                                                 if (roominfo) {
//                                                     roominfo.disconnect();
//                                                     navigate('/screen');
//                                                 }
//                                             }}
//                                         >
//                                             <i className="bi bi-stop-circle-fill me-2"></i> 
//                                             Stop Streaming
//                                         </button>
//                                     ) : (
//                                         <button 
//                                             className="btn btn-success focus-ring focus-ring-success rounded-pill" 
//                                             title="Start Streaming" 
//                                             onClick={joinRoom}
//                                         >
//                                             <i className="bi bi-play-circle-fill me-2"></i> 
//                                             Start Streaming
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {/* MIUI Browser Warning Modal */}
//             <Modal 
//                 centered 
//                 show={showMIUIWarning} 
//                 backdrop="static" 
//                 keyboard={false} 
//                 contentClassName="rounded-3"
//             >
//                 <Modal.Body className="border-0 p-4">
//                     <div className="text-center">
//                         <div className="text-danger mb-3">
//                             <i className="bi bi-exclamation-triangle-fill display-1"></i>
//                         </div>
//                         <h5 className="fw-bold mb-3 text-danger">Unsupported Browser</h5>
//                         <div className="text-muted mb-4">
//                             MIUI Browser is not compatible with this application. 
//                             Please use a modern browser like <strong>Chrome</strong>, <strong>Firefox</strong>, or <strong>Safari</strong> for the best experience.
//                         </div>
//                         <div className="d-grid">
//                             <button 
//                                 className="btn btn-primary btn-lg" 
//                                 onClick={() => setShowMIUIWarning(false)}
//                             >
//                                 <i className="bi bi-check-lg me-2"></i>
//                                 I Understand
//                             </button>
//                         </div>
//                     </div>
//                 </Modal.Body>
//             </Modal>

//             {/* Enhanced Connection Issues Modal */}
//             <Modal 
//                 centered 
//                 show={mobileshow} 
//                 backdrop="static" 
//                 keyboard={false} 
//                 contentClassName="rounded-3"
//             >
//                 <Modal.Body className="border-0 p-4">
//                     <div className="text-center">
//                         <div className="text-warning mb-3">
//                             <i className="bi bi-wifi-off display-1"></i>
//                         </div>
//                         <h5 className="fw-bold mb-3">Connection Issue</h5>
//                         <div className="text-muted mb-4">
//                             {warningcontent}
//                         </div>
//                         {reconnectBtn && (
//                             <div className="d-grid gap-2">
//                                 <button 
//                                     className="btn btn-primary btn-lg" 
//                                     onClick={reconnectConnection}
//                                     disabled={!isOnline}
//                                 >
//                                     <i className="bi bi-arrow-clockwise me-2"></i>
//                                     {isOnline ? 'Reconnect' : 'Waiting for connection...'}
//                                 </button>
                                
//                                 {connectionAttempts > 0 && (
//                                     <small className="text-muted">
//                                         Attempt {connectionAttempts} of 5
//                                     </small>
//                                 )}
//                             </div>
//                         )}
//                     </div>
//                 </Modal.Body>
//             </Modal>

//             <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} theme="colored" />

//             <style>{`
//                 .mobile-stream-page {
//                     min-height: 100vh;
//                     background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
//                 }
                
//                 .preview-wrapper {
//                     border: 3px solid #dee2e6;
//                     background: #000;
//                     border-radius: 0.5rem;
//                 }
                
//                 .badge.small {
//                     font-size: 0.7em;
//                     padding: 0.25em 0.4em;
//                 }
                
//                 #rearVideo {
//                     border-radius: inherit;
//                 }
                
//                 .btn {
//                     touch-action: manipulation;
//                     -webkit-tap-highlight-color: transparent;
//                 }
                
//                 @media (max-width: 576px) {
//                     .container-xl {
//                         padding: 0.5rem;
//                     }
                    
//                     .brand-name {
//                         font-size: 14px;
//                     }

//                     .preview-wrapper {
//                        border-width: 2px;
//                   }
//                }
//             `}</style>
//         </>
//    );
// };

// export default Mobile;
// Code end